import { isSafari } from '@/lib/utils';

export const getEffectiveDuration = (
  audio: HTMLAudioElement | null,
  fallbackDuration: number | null,
): number | null => {
  if (!audio) return null;
  const { duration, seekable } = audio;

  // Safari Infinity fix: prefer fallbackDuration, then seekable, then nothing
  if (isSafari() && !isFinite(duration)) {
    if (fallbackDuration && fallbackDuration > 0) return fallbackDuration;

    if (seekable.length > 0) {
      const end = seekable.end(seekable.length - 1);

      if (isFinite(end) && end > 0) return end;
    }

    return null;
  }

  // Safari: protect against seekable > duration
  if (isSafari() && isFinite(duration) && duration > 0 && seekable.length > 0) {
    const end = seekable.end(seekable.length - 1);

    if (isFinite(end) && end > 0) return Math.min(duration, end);

    return duration;
  }

  // Other browsers: prefer seekable if available
  if (seekable.length > 0) {
    const end = seekable.end(seekable.length - 1);

    if (isFinite(end) && end > 0) return end;
  }

  if (isFinite(duration) && duration > 0) return duration;

  return null;
};

export const clampTimeToPlayable = (
  time: number,
  audio: HTMLAudioElement | null,
  fallbackDuration: number | null,
): number => {
  if (!audio) return 0;
  const max = getEffectiveDuration(audio, fallbackDuration);
  let result = Math.max(0, time);

  if (max !== null) {
    result = Math.min(result, max);
  }

  if (isSafari()) {
    const rawDuration = audio.duration;
    let effective = rawDuration;

    if (!isFinite(rawDuration) && fallbackDuration && fallbackDuration > 0) {
      effective = fallbackDuration;
    }

    if (isFinite(effective) && effective > 0) {
      result = Math.min(result, effective);
    }
  }

  return result;
};
